import { useState, useEffect } from 'react';
import { NewTransactionForm } from './components/NewTransactionForm';
import { TransactionTable } from './components/TransactionTable';
import { useTelegram } from './hooks/useTelegram';

type Screen = 'form' | 'table';

function App() {
  const [currentScreen, setCurrentScreen] = useState<Screen>('form');
  const { tg, user } = useTelegram();

  console.log('📱 App render - tg:', tg, 'user:', user);

  useEffect(() => {
    if (tg) {
      tg.ready();
      tg.expand();
    }
  }, [tg]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-telegram-bg">
        <div className="text-center">
          <div className="w-16 h-16 bg-telegram-button rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
            <span className="text-2xl">💰</span>
          </div>
          <h1 className="text-2xl font-bold text-telegram-text mb-2">Accounting Bot</h1>
          <p className="text-telegram-hint">Подключение к Telegram...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-telegram-bg">
      {/* Mobile-first navigation */}
      <nav className="sticky top-0 bg-telegram-bg/80 backdrop-blur-md border-b border-gray-200 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3">
          {/* User info - компактный для мобильных */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-telegram-button rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-bold">
                  {user.first_name?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold text-telegram-text">Accounting Bot</h1>
                <p className="text-xs text-telegram-hint">
                  {user.first_name} (ID: {user.id})
                </p>
              </div>
              <div className="sm:hidden">
                <h1 className="text-lg font-bold text-telegram-text">Accounting</h1>
              </div>
            </div>
          </div>

          {/* Navigation tabs */}
          <div className="flex gap-1 bg-telegram-secondary-bg/50 p-1 rounded-xl">
            <button
              onClick={() => setCurrentScreen('form')}
              className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-all duration-200 ${
                currentScreen === 'form'
                  ? 'bg-white text-telegram-button shadow-sm'
                  : 'text-telegram-text hover:bg-white/50'
              }`}
            >
              <span className="sm:hidden">➕</span>
              <span className="hidden sm:inline">Новая транзакция</span>
            </button>
            <button
              onClick={() => setCurrentScreen('table')}
              className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-all duration-200 ${
                currentScreen === 'table'
                  ? 'bg-white text-telegram-button shadow-sm'
                  : 'text-telegram-text hover:bg-white/50'
              }`}
            >
              <span className="sm:hidden">📊</span>
              <span className="hidden sm:inline">Транзакции</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main className="pb-safe">
        <div className="max-w-7xl mx-auto px-4">
          {/* Desktop-optimized layout */}
          <div className="lg:grid lg:grid-cols-12 lg:gap-8 lg:h-[calc(100vh-120px)]">

            {/* Desktop sidebar navigation (hidden on mobile) */}
            <div className="hidden lg:block lg:col-span-3 xl:col-span-2">
              <div className="sticky top-28 space-y-4">
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                  <h3 className="font-semibold text-gray-900 mb-4">Навигация</h3>
                  <nav className="space-y-2">
                    <button
                      onClick={() => setCurrentScreen('form')}
                      className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 ${
                        currentScreen === 'form'
                          ? 'bg-telegram-button text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-lg">➕</span>
                        <span className="font-medium">Новая транзакция</span>
                      </div>
                    </button>
                    <button
                      onClick={() => setCurrentScreen('table')}
                      className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 ${
                        currentScreen === 'table'
                          ? 'bg-telegram-button text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-lg">📊</span>
                        <span className="font-medium">Все транзакции</span>
                      </div>
                    </button>
                  </nav>
                </div>

                {/* Quick stats card */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-6 border border-blue-200">
                  <h3 className="font-semibold text-blue-900 mb-2">Быстрый доступ</h3>
                  <p className="text-sm text-blue-700">
                    Создавайте транзакции одним кликом
                  </p>
                </div>
              </div>
            </div>

            {/* Main content area */}
            <div className="lg:col-span-9 xl:col-span-10">
              <div className="lg:bg-white lg:rounded-2xl lg:shadow-sm lg:border lg:border-gray-100 lg:min-h-[600px]">
                {currentScreen === 'form' && (
                  <div className="animate-fadeIn">
                    <NewTransactionForm />
                  </div>
                )}
                {currentScreen === 'table' && (
                  <div className="animate-fadeIn lg:p-6">
                    <TransactionTable />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default App;